import { useCallback } from 'react';
import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { Parser } from 'expr-eval';

import { Notistack } from '@shared/components/Notistack/Notistack';
import { newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { Modal } from '@shared/components/modals/Modal';
import { Wrapper } from '@styles/CommonStyles';
import { calculationType } from '@modules/admin/Tools/LenderUtility/tabs/DocFees/DocFeesTable/interfaces';
import { requestUpdateProductRules } from '@modules/admin/Tools/LenderUtility/tabs/ProductRules/queries';
import { IUpdateProductExclusionRulesPayload, TProductTypeExclusionRule } from '@modules/admin/Tools/LenderUtility/interfaces';
import { statesList } from '@shared/constants/statesList';
import { IFormulaFormValues, IFormulaProps, IFormulaVariables, stateProductRestrictionFormulaVariables } from '../interfaces';
import { formulaFormValidationSchema } from '../validation';
import { requestCreateStateProductRestriction, requestUpdateStateProductRestriction } from './requests';
import { ProductRestrictionsFormulaForm } from './ProductRestrictionsFormulaForm';

export const variablesColumns = [
  dataGridCol('id', {
    headerName: 'Path',
    width: 100,
  }),
  dataGridCol('lenderVariable', {
    flex: 1,
    textWrap: true,
  }),
].map((column) => ({ ...column, resizable: false }));

const variables: IFormulaVariables[] = stateProductRestrictionFormulaVariables;

const parser = new Parser({
  operators: {
    add: true,
    concatenate: true,
    conditional: true,
    divide: true,
    factorial: true,
    multiply: true,
    power: true,
    remainder: true,
    subtract: true,
    logical: true,
    comparison: true,
    in: false,
    assignment: false,
  },
});

parser.functions.if = (expr: any, thenVal: any, elseVal: any) => {
  return expr ? thenVal : elseVal;
};

export const ProductRestrictionFormulaModal = ({
  modalData,
  lenderProductRulesMutate,
  lenderId,
  stateProductRestrictionsMutate,
  handleClose,
  productId,
  setLoading,
  loading,
  apiRef,
}: IFormulaProps) => {
  const intialValues: IFormulaFormValues = {
    id: modalData?.id ?? '',
    state: modalData?.state ?? '',
    maxAllowedFormula: modalData?.maxAllowedFormula ?? '',
    calculationType: calculationType.Formula,
    maxAllowedAmount: 0,
  };

  const isValidFormula = useCallback((formula: string) => {
    try {
      const expr = parser.parse(formula);

      // evaluate with mock values for variables (all values set to 1)
      const testVariables: Record<string, number> = {};
      variables.forEach((v) => {
        testVariables[v.id] = 1;
      });

      const value = expr.evaluate(testVariables);

      return !!value || value === 0;
    } catch {
      Notistack.generalError('Invalid Formula');
      return false;
    }
  }, []);

  const handleSubmit = useCallback(
    async (data: IFormulaFormValues) => {
      const isValid = isValidFormula(data.maxAllowedFormula);

      if (!isValid) return;
      setLoading(true);
      if (!lenderId && stateProductRestrictionsMutate) {
        const payload = { ...modalData, ...data };
        const formattedCalculationType =
          typeof payload.calculationType === 'string'
            ? Number(calculationType[payload.calculationType as keyof typeof calculationType])
            : payload.calculationType;
        if (payload?.id === newGridRecordId) {
          const requestPayload = {
            name: payload.productType,
            state: payload.state,
            maxAllowedAmount: payload.maxAllowedAmount,
            maxAllowedFormula: payload.maxAllowedFormula,
            isAllowed: payload.isAllowed ?? true,
            calculationType: formattedCalculationType,
          };
          await requestCreateStateProductRestriction(requestPayload);
        } else {
          const updateRequestPayload = {
            id: payload.id,
            name: payload.productType,
            state: payload.state,
            maxAllowedAmount: payload.maxAllowedAmount,
            maxAllowedFormula: payload.maxAllowedFormula,
            isAllowed: payload.isAllowed,
            calculationType: formattedCalculationType,
          };
          await requestUpdateStateProductRestriction(updateRequestPayload);
        }
        stateProductRestrictionsMutate();
      }
      if (lenderId && lenderProductRulesMutate) {
        const payload = { ...modalData, ...data };
        const formattedCalculationType =
          typeof payload.calculationType === 'string'
            ? Number(calculationType[payload.calculationType as keyof typeof calculationType])
            : payload.calculationType;
        if (payload?.id === newGridRecordId) {
          const requestPayload = {
            name: payload.productType,
            state: payload.state,
            maxAllowedAmount: payload.maxAllowedAmount,
            maxAllowedFormula: payload.maxAllowedFormula,
            isAllowed: payload.isAllowed ?? true,
            calculationType: formattedCalculationType,
          };
          await requestCreateStateProductRestriction(requestPayload);
        } else {
          const payload = { ...modalData, ...data };
          const records = Array.from(apiRef.current.getRowModels().values());
          const allStateValues = statesList.map((state) => state.value);
          const formattedCalculationType =
            typeof payload.calculationType === 'string'
              ? calculationType[payload.calculationType as keyof typeof calculationType]
              : payload.calculationType;
          const updatedTypes = records.map((record) => {
            const isFormula = record.calculationType === calculationType.Formula;
            return {
              ...record,
              calculationType: record.id === payload.id ? formattedCalculationType : record.calculationType,
              maxAmount: isFormula ? null : Number(record.maxAmount) || 0,
              maxAllowedFormula: record.id === payload.id ? payload.maxAllowedFormula : record.maxAllowedFormula,
              isCurrent: true,
              isDeleted: false,
              excludedStates: record.excludedStates.includes('All') ? allStateValues : record.excludedStates,
            };
          });
          const requestPayload: IUpdateProductExclusionRulesPayload = {
            ...payload,
            calculationType: formattedCalculationType,
            products: updatedTypes as TProductTypeExclusionRule[],
          };

          await requestUpdateProductRules(lenderId, requestPayload);
        }
        lenderProductRulesMutate();
      }
      setLoading(false);
      handleClose();
    },
    [modalData, productId]
  );

  const onClose = useCallback(() => {
    const id = modalData?.id ?? newGridRecordId;

    apiRef.current.setEditCellValue({ id, field: 'calculationType', value: null });
    handleClose();
  }, []);

  return (
    <Modal
      hideBackdrop
      title="Add / Edit Formula"
      open={!!modalData}
      dialogMinHeight="86%"
      dialogMinWidth="80%"
      onClose={onClose}
    >
      <Grid
        container
        marginTop={2}
        justifyContent="space-between"
      >
        <Grid
          item
          xs={6}
        >
          <Formik
            enableReinitialize
            initialValues={intialValues}
            validationSchema={formulaFormValidationSchema}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <ProductRestrictionsFormulaForm
                  loading={loading}
                  handleClose={onClose}
                  {...props}
                />
              </Form>
            )}
          </Formik>
        </Grid>
        <Grid
          item
          xs={6}
          mt="-10px"
        >
          <Wrapper>
            <PhoenixGrid
              noButton
              withHeader
              autoHeight
              hideFooter
              isFlexible
              autoRowHeight
              disableSelectionOnClick
              name="Formula Variable"
              pageSize={15}
              columns={variablesColumns}
              rows={variables}
            />
          </Wrapper>
        </Grid>
      </Grid>
    </Modal>
  );
};
